import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValueChangedEvent, INumberCellEditorParams } from 'ag-grid-community';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import { CELL_EDITOR_TYPES } from '@/components/UI/TableListEditable/TableListEditableConstants';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import { statusCampaign, statusSubCampaign } from '@/helpers/constants';
import useAppLanguage from '@/hooks/useAppLanguage';
import useToastContext from '@/hooks/useToastContext';
import administrationServices from '@/services/administrationServices';
import titleService from '@/services/titleService';
import styles from './WorkflowDuration.module.scss';

type WorkflowDurationRow = {
  id: string;
  statusName: string;
  duration: string;
};

type WorkflowDurationSection = {
  key: string;
  title: string;
  rows: WorkflowDurationRow[];
};

const STATUS_NAME_FIELD = 'statusName' as const;
const DURATION_FIELD = 'duration' as const;
const ID_FIELD = 'id' as const;
const STATUS_FIELD = 'status' as const;
const CAMPAIGN_PREFIX = 'campaign' as const;
const SUBCAMPAIGN_PREFIX = 'subcampaign' as const;
const SUBCAMPAIGN_TRANSLATION_PREFIX = 'subCampaign' as const;

const WorkflowDuration: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const lang = useAppLanguage();
  const [workflowDuration, setWorkflowDuration] = useState<Array<{ status: string; durationDays: number }>>([]);

  const isCampaignStatus = (status: string): boolean => {
    return Object.prototype.hasOwnProperty.call(statusCampaign, status);
  };

  const isSubCampaignStatus = (status: string): boolean => {
    return Object.prototype.hasOwnProperty.call(statusSubCampaign, status);
  };

  const { campaignRows, subCampaignRows } = useMemo(() => {
    const mapRows = (
      items: Array<{ status: string; durationDays: number }>,
      rowPrefix: string,
      translationPrefix: string,
    ) =>
      items.map((item) => ({
        [ID_FIELD]: `${rowPrefix}-${item.status}`,
        [STATUS_NAME_FIELD]: t(`${translationPrefix}.${item.status}`),
        [DURATION_FIELD]: item.durationDays.toString(),
        [STATUS_FIELD]: item.status,
      }));

    return {
      campaignRows: mapRows(
        workflowDuration.filter((item) => isCampaignStatus(item.status)),
        CAMPAIGN_PREFIX,
        CAMPAIGN_PREFIX,
      ),
      subCampaignRows: mapRows(
        workflowDuration.filter((item) => isSubCampaignStatus(item.status)),
        SUBCAMPAIGN_PREFIX,
        SUBCAMPAIGN_TRANSLATION_PREFIX,
      ),
    };
  }, [workflowDuration, t]);

  const handleSaveDurationChange = useCallback(({ rowData, value }: { rowData: Record<string, unknown>; value: string }) => {
    const status = rowData[STATUS_FIELD] as string | undefined;
    if (!status) return;

    administrationServices.saveWorkflowStatusDuration(status, value)
      .catch((error) => addToast(error.message, 'ERROR'));
  }, [addToast]);

  const handleCellValueChanged = useCallback((event: CellValueChangedEvent) => {
    if (event.colDef.field !== DURATION_FIELD || !event.data) return;

    const currentDuration = event.data[DURATION_FIELD as keyof typeof event.data];

    handleSaveDurationChange({
      rowData: event.data as Record<string, unknown>,
      value: String(currentDuration ?? ''),
    });
  }, [handleSaveDurationChange]);

  const columns = useMemo(() => ([
    {
      name: STATUS_NAME_FIELD,
      title: t('administration.workflowDuration.statusName'),
      wrapText: true,
      flex: 2,
    },
    {
      name: DURATION_FIELD,
      title: t('administration.workflowDuration.durationTime'),
      flex: 1,
      editable: true,
      cellEditor: CELL_EDITOR_TYPES.NUMBER,
      cellEditorParams: {
        precision: 0,
      } as INumberCellEditorParams,
    },
  ]), [t]);

  useEffect(() => {
    administrationServices.getWorflowStatusDuration().then((data) => {
      setWorkflowDuration(data.workflowStatusDurations);
    });
  }, []);

  useEffect(() => {
    titleService.updatePageTitle(t('dashboard.administration'), t('menu.workflowDuration'));
  }, [lang, t]);

  const sections = useMemo<WorkflowDurationSection[]>(() => {
    return [
      {
        key: CAMPAIGN_PREFIX,
        title: t('administration.workflowDuration.campaignWorkflow'),
        rows: campaignRows,
      },
      {
        key: SUBCAMPAIGN_PREFIX,
        title: t('administration.workflowDuration.subCampaignWorkflow'),
        rows: subCampaignRows,
      },
    ];
  }, [campaignRows, subCampaignRows, t]);

  return (
    <div className={styles.WorkflowDurationPage}>
      {sections.map((section) => (
        <AccordionPanel key={section.key} title={`${section.title} (${section.rows.length})`}>
          <div className={styles.GridContainer}>
            <TableListEditableNew
              list={section.rows || []}
              columns={columns}
              isPaginated={false}
              onCellValueChanged={handleCellValueChanged}
            />
          </div>
        </AccordionPanel>
      ))}
    </div>
  );
};

export default WorkflowDuration;
